/**
 * Mirrors of the C# Logistics › Goods Receipt DTO records, field for field.
 *
 * Its own file rather than more of `stockTypes.ts`, on the reason that file gives for splitting
 * from `inventoryTypes.ts`: a module that would double the length of a file nobody could then
 * see the shape of gets its own.
 */

import type { ListQuery } from '../../administration/users/userTypes';
import type { InventoryItemKind } from './inventoryTypes';

/**
 * Where a delivery has got to. Enums serialize as int, so the numbers are the contract.
 *
 * <b>These must be NUMBERS, not a string union.</b> No `JsonStringEnumConverter` is configured,
 * so a union of `'PendingInspection' | …` would typecheck perfectly against JSON the compiler
 * cannot see and make every comparison silently false — the defect the approval profile shipped
 * with, where a pending request drew no decision button at all.
 *
 * FACET values are the exception and stay strings: the server emits enum NAMES there and parses
 * them with `Enum.TryParse`.
 */
export const GOODS_RECEIPT_STATUS = {
  pendingInspection: 0,
  received: 1,
  cancelled: 2,

  /**
   * Every line inspected and stocked, not yet closed against the order.
   *
   * **APPENDED at 3**, because the numbers are stored — see the server enum, where the model
   * gap this closed is argued. A member the server can send and this const does not name is
   * one every comparison answers `false` for, which `PO_STATUS` shipped once.
   */
  inspected: 3,
} as const;

export type GoodsReceiptStatus = (typeof GOODS_RECEIPT_STATUS)[keyof typeof GOODS_RECEIPT_STATUS];

/**
 * WHERE a delivery came from — the first thing the form asks.
 *
 * NUMBERS, because the wire carries numbers: no `JsonStringEnumConverter` is configured, so a
 * string union here would typecheck perfectly and make every comparison silently false. That
 * shipped once in the approvals frontend and once in purchasing, on the same day.
 */
export const GOODS_RECEIPT_SOURCE = {
  purchaseOrder: 0,
  donation: 1,
  stockTransfer: 2,
} as const;

export type GoodsReceiptSource =
  (typeof GOODS_RECEIPT_SOURCE)[keyof typeof GOODS_RECEIPT_SOURCE];

/**
 * Whether this source delivers against a purchase order.
 *
 * The frontend twin of `GoodsReceiptSources.UsesPurchaseOrder`, and the one place the wizard
 * branches — which step list is drawn, whether the PO picker is required, and whether a line
 * names an order line or an item.
 */
export function usesPurchaseOrder(source: GoodsReceiptSource): boolean {
  return source === GOODS_RECEIPT_SOURCE.purchaseOrder;
}

/** The reader's word for a source. One definition, mirroring the domain's. */
export const GOODS_RECEIPT_SOURCE_LABEL: Record<GoodsReceiptSource, string> = {
  [GOODS_RECEIPT_SOURCE.purchaseOrder]: 'Receive from PO',
  [GOODS_RECEIPT_SOURCE.donation]: 'Donation',
  [GOODS_RECEIPT_SOURCE.stockTransfer]: 'Stock Transfer',
};

/*
  NO LABEL MAP HERE, for the reason `inventoryAuditTypes` states: `statusLabel` is resolved
  server-side from `GoodsReceiptStatuses.Label` and rides on every row, and the `stage` facet's
  values arrive carrying their own labels — so a client map would have no consumer at all. One
  vocabulary, defined once, on the server.
*/

/** Whether the delivery is still open — recorded, and not yet registered or abandoned. */
export function isReceiptOpen(status: GoodsReceiptStatus): boolean {
  return status === GOODS_RECEIPT_STATUS.pendingInspection;
}

/**
 * Which of the picker's two tabs an order falls under. Derived server-side from its quantities.
 */
export const PURCHASE_ORDER_STAGE = {
  awaitingDelivery: 0,
  partiallyReceived: 1,
} as const;

export type PurchaseOrderStage =
  (typeof PURCHASE_ORDER_STAGE)[keyof typeof PURCHASE_ORDER_STAGE];

/**
 * The OFFICIAL inspection's verdict — one closed list of five.
 *
 * Product call, 05 Oct 2026, in the reader's own words: the failed reasons *should only be*
 * expired/near expiry, damaged, incorrect item and packaging compromised, with *Good Quality in
 * the list, to denote accepted*.
 *
 * NUMBERS, because the wire carries numbers: no `JsonStringEnumConverter` is configured, so a
 * string union here would typecheck perfectly and make every comparison silently false — the
 * defect `approvalTypes.ts` shipped once and `PO_STATUS` shipped again.
 */
export const GOODS_RECEIPT_INSPECTION_STATUS = {
  goodQuality: 0,
  expiredOrNearExpiry: 1,
  damaged: 2,
  incorrectItem: 3,
  packagingCompromised: 4,
} as const;

export type GoodsReceiptInspectionStatus =
  (typeof GOODS_RECEIPT_INSPECTION_STATUS)[keyof typeof GOODS_RECEIPT_INSPECTION_STATUS];

/**
 * The form's options, in the order it offers them — **Good Quality FIRST**, because it is the
 * common verdict and the one the four failures are read against.
 */
export const GOODS_RECEIPT_INSPECTION_OPTIONS: readonly {
  value: GoodsReceiptInspectionStatus;
  label: string;
}[] = [
  { value: GOODS_RECEIPT_INSPECTION_STATUS.goodQuality, label: 'Good Quality' },
  { value: GOODS_RECEIPT_INSPECTION_STATUS.expiredOrNearExpiry, label: 'Expired/Near expiry' },
  { value: GOODS_RECEIPT_INSPECTION_STATUS.damaged, label: 'Damaged' },
  { value: GOODS_RECEIPT_INSPECTION_STATUS.incorrectItem, label: 'Incorrect Item' },
  { value: GOODS_RECEIPT_INSPECTION_STATUS.packagingCompromised, label: 'Packaging Compromised' },
];

/**
 * Whether a verdict means the goods become stock.
 *
 * **ONE definition, mirroring `GoodsReceiptInspectionStatuses.IsAccepted` on the server.** The
 * two are one rule written twice — unavoidable across the wire — so a sixth member owes both.
 */
export function isGoodsReceiptVerdictAccepted(status: GoodsReceiptInspectionStatus): boolean {
  return status === GOODS_RECEIPT_INSPECTION_STATUS.goodQuality;
}

export interface GoodsReceiptListQuery extends ListQuery {
  /** Comma-separated `GoodsReceiptSource` names. */
  source?: string;
  /** The module's own lifecycle axis. `status` on the base is the ROW's live-or-deleted state. */
  stage?: string;
  supplier?: string;
  facility?: string;
  /** Scope to one order — used by the order profile's own deliveries tab. */
  purchaseOrderId?: string;
}

export interface GoodsReceiptListItem {
  id: string;
  /** The anchor. NOT the supplier, which the source anchors on — two deliveries would read alike. */
  receiptNumber: string;
  /** WHERE it came from. The first thing the form asks. */
  source: GoodsReceiptSource;
  sourceLabel: string;

  /** The order delivered against. Absent on a donation or a transfer. */
  purchaseOrderId: string | null;
  orderNumber: string | null;
  supplierId: string | null;
  supplierName: string | null;

  /** Who gave it. Set on a DONATION only. */
  donorName: string | null;

  /** The store it came out of. Set on a TRANSFER only. */
  sourceFacilityName: string | null;

  /** WHO it came from, whichever of the three a source names. Derived server-side. */
  counterparty: string;
  status: GoodsReceiptStatus;
  statusLabel: string;
  deliveredOn: string;
  lineCount: number;
  /**
   * How many of those lines the OFFICIAL inspection has ruled on — what the register's progress
   * ring and its `Inspected` column are a fraction of. LINES, never a summed quantity: a
   * delivery's lines are measured in their own units.
   */
  inspectedLineCount: number;
  totalReceived: number;
  /** NULL while ANY line is uninspected — a partial sum reads as a finished figure. */
  totalAccepted: number | null;
  /** The one store the lines agree on, else how many they span. Derived server-side. */
  destination: string | null;
  items: string | null;
  invoiceNumber: string | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

/**
 * How a delivered line NAMES its item: the provider on top, the governed type under it.
 *
 * Product call, 05 Oct 2026, in two steps. First *the item name should be concatinated in the
 * type. concat the manufacturer/service provider*, which shipped as `AI Subscription — Claude`
 * on one line; then *revise, make the type below the item*, which is this.
 *
 * **An item has NO name column of its own.** `itemName` is its TYPE's name — the governed
 * vocabulary — so two AI Subscriptions both read `AI Subscription` and only the manufacturer
 * says WHICH. So the manufacturer leads: it is what identifies this line's item, and the type
 * is the context under it. That is the Stock Levels anchor's own shape, where the identity
 * leads and `unit · SKU` sits beneath.
 *
 * **The fallback never leaves a blank title.** An item with no manufacturer puts the TYPE on
 * the primary line and draws no second one, rather than a title nobody can read over a type.
 * `<StackedCell>` renders a secondary only when it is non-empty, so an absent one costs no
 * ragged row.
 *
 * **ONE definition, because the cell, the SEARCH and the SORT all read it.** Written three
 * times they would eventually disagree about which value leads — and the rule is that a title
 * the search cannot reach is a defect, which only holds while all three read one answer.
 */
export function goodsReceiptItemIdentity(
  itemName: string,
  manufacturer: string | null,
): { primary: string; secondary?: string } {
  const who = (manufacturer ?? '').trim();

  return who ? { primary: who, secondary: itemName } : { primary: itemName };
}

export interface GoodsReceiptLineSerial {
  id: string;
  serialNumber: string;
  /** True accepted, false rejected, NULL not yet inspected. */
  /** This serial's verdict. NULL = not yet inspected. */
  inspectionStatus: GoodsReceiptInspectionStatus | null;
  inspectionStatusLabel: string | null;
  /** DERIVED server-side from the verdict, so the two cannot disagree. */
  isAccepted: boolean | null;
  /** What the inspector wrote about THIS serial — the table's third column. */
  inspectionNotes: string | null;
  resultingStockUnitId: string | null;
}

export interface GoodsReceiptLine {
  id: string;
  /** The order line delivered against. Absent on a donation or transfer line. */
  purchaseOrderItemId: string | null;
  inventoryItemId: string;
  itemName: string;
  /** Who provides it — what tells two items of one type apart. */
  manufacturer: string | null;
  sku: string;
  unitCode: string;
  tracksSerial: boolean;
  tracksExpiry: boolean;
  storageFacilityId: string | null;
  storageFacilityName: string | null;
  batchNumber: string | null;
  expiryDate: string | null;
  /** What the ORDER asked for. NULL where there is no order — the grid draws an em dash. */
  quantityOrdered: number | null;
  quantityReceived: number;
  /**
   * The DOCK's breakdown — the FIRST of the two inspections.
   *
   * Recorded by the receiving clerk as the boxes are opened. It does NOT decide what becomes
   * stock: `quantityAccepted` is the official inspection's figure and stays null until somebody
   * inspects. A divergence between the two is a finding about the delivery, which is why both
   * are drawn on the profile rather than one overwriting the other.
   */
  quantityExpired: number;
  quantityDamaged: number;
  quantityWrongItem: number;
  /** Get-only on the server, over the three causes. */
  quantityUnusable: number;
  /** Received minus the three. Get-only on the server, so it cannot contradict them. */
  quantityGood: number;
  /** NULL until inspected — not zero, which is a finding. */
  quantityAccepted: number | null;
  quantityRejected: number | null;
  /** The verdict. On a PARTIAL line it describes the REJECTED part. */
  inspectionStatus: GoodsReceiptInspectionStatus | null;
  inspectionStatusLabel: string | null;
  /** Rejected goods are tagged for return. DERIVED from the rejected quantity. */
  awaitsReturnToSupplier: boolean;
  /** When this line's stock was created. NULL until it has been. */
  stockedAt: string | null;
  /** What blocks a re-inspection — the stock exists and the ledger is append-only. */
  isStocked: boolean;
  inspectionNotes: string | null;
  unitCost: number | null;
  lineTotal: number | null;
  /** What is still open on the order line once this receipt is registered. A full line reads 0. */
  /** What is still open ON THE ORDER. NULL where there is none. */
  balanceAfter: number | null;
  resultingStockBatchId: string | null;
  notes: string | null;
  serials: GoodsReceiptLineSerial[];
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface GoodsReceiptDetail {
  id: string;
  receiptNumber: string;
  source: GoodsReceiptSource;
  sourceLabel: string;
  purchaseOrderId: string | null;
  orderNumber: string | null;
  supplierId: string | null;
  supplierName: string | null;
  donorName: string | null;
  sourceStorageFacilityId: string | null;
  sourceFacilityName: string | null;
  /** WHO it came from. Derived server-side. */
  counterparty: string;
  status: GoodsReceiptStatus;
  statusLabel: string;
  deliveredOn: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  notes: string | null;
  cancellationReason: string | null;
  lines: GoodsReceiptLine[];
  /** Get-only over the lines, so no write path can contradict them. Null where nothing is priced. */
  totalValue: number | null;
  /**
   * What the WHOLE purchase order comes to, summed server-side from its own live lines.
   *
   * Null on a donation and a transfer, which name no order. It replaced a stored
   * `invoiceAmount` this form used to collect — see the detail DTO's own remarks for the
   * partial-delivery cost that comes with reading the order's total rather than a bill.
   */
  orderTotalAmount: number | null;
  /** What gates registration. */
  isFullyInspected: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

/** An order the wizard may receive against — step one's picker feed. */
export interface ReceivableOrderOption {
  id: string;
  orderNumber: string;
  supplierId: string;
  supplierName: string;
  sourceRequestNumbers: string | null;
  items: string | null;
  lineCount: number;
  openLineCount: number;
  totalOrdered: number;
  totalReceived: number;
  /** What the picker draws its progress bar from. Get-only over the two figures beside it. */
  receivedPercent: number;
  expectedDeliveryDate: string | null;
  stage: PurchaseOrderStage;
}

/** One open line on a receivable order — step two's grid feed. */
export interface ReceivableOrderLine {
  purchaseOrderItemId: string;
  inventoryItemId: string;
  itemName: string;
  sku: string;
  unitCode: string;
  kind: InventoryItemKind;
  tracksSerial: boolean;
  tracksExpiry: boolean;
  /** Whether the kind is kept anywhere. A licence is not, so the store is refused. */
  sitsSomewhere: boolean;
  orderedQuantity: number;
  receivedQuantity: number;
  /** What is still outstanding. The grid caps the receive box at this. */
  openQuantity: number;
  unitCost: number;
  purchaseRequestNumber: string;
}

export interface CreateGoodsReceiptSerial {
  serialNumber: string;
}

export interface CreateGoodsReceiptLine {
  /** The order line. Sent on a purchase-order receipt ONLY. */
  purchaseOrderItemId?: string | null;

  /** What arrived. Sent on a donation or transfer ONLY. */
  inventoryItemId?: string | null;
  storageFacilityId: string | null;
  batchNumber: string | null;
  expiryDate: string | null;
  /** ZERO means the reader did not tick this line. The server drops it after validating. */
  quantityReceived: number;
  /**
   * The DOCK's unusable breakdown. Zero is the ordinary answer.
   *
   * <b>There is no `quantityGood` to send</b> — the server derives it as received minus these
   * three, so a request cannot state a good count that disagrees with its own breakdown.
   */
  quantityExpired: number;
  quantityDamaged: number;
  quantityWrongItem: number;
  unitCost: number | null;
  notes: string | null;
  serials: CreateGoodsReceiptSerial[];
}

/**
 * Record a delivery. Creates it PENDING INSPECTION — nothing becomes stock.
 *
 * <b>There is no field for the receipt number</b>, which is what makes it unspoofable rather than
 * merely unspoofed.
 */
export interface CreateGoodsReceiptRequest {
  source: GoodsReceiptSource;

  /** Required on a purchase-order receipt, refused on anything else. */
  purchaseOrderId?: string | null;

  /** Required on a donation, refused on anything else. */
  donorName?: string | null;

  /** Required on a transfer, refused on anything else. */
  sourceStorageFacilityId?: string | null;
  deliveredOn: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  notes: string | null;
  lines: CreateGoodsReceiptLine[];
}

export interface InspectSerialVerdict {
  serialId: string;
  inspectionStatus: GoodsReceiptInspectionStatus;
  inspectionNotes: string | null;
}

/** Record the verdict on ONE line, addressed by its own id. */
export interface InspectGoodsReceiptLineRequest {
  /** REFUSED on a serialized line, where the per-serial verdicts decide it. */
  quantityAccepted: number | null;
  inspectionStatus: GoodsReceiptInspectionStatus | null;
  inspectionNotes: string | null;
  /** REQUIRED on a serialized line, refused otherwise. */
  serials: InspectSerialVerdict[];
}

export interface CancelGoodsReceiptRequest {
  reason: string | null;
}
